import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { connect } from 'react-redux';
import FileSaver from 'file-saver';
import {
  Drawer as AntDrawer,
  Form,
  Button,
  Col,
  Row,
  Input,
  Select,
  Spin,
  message,
  Checkbox,
} from 'antd';
import selector from './Drawer.selector';
import {
  close,
  handleSubmit as sendForm,
  changePaymentType,
  changePaymentVendor,
} from './Drawer.reducer';
import { colors } from '../../../utils/variables.styles';
import withTranslation from '../../../components/WithTranslation/WithTranslation';
import { useModal } from '../../../contexts/ModalContext';

const { Option } = Select;

const TabRow = styled.div`
  display: flex;
`;

const PaymentButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 50px !important;
  max-width: 117px;
  width: 100%;
  border-radius: 4px !important;
  padding: 5px;
  border: 1px solid #d9d9d9 !important;

  &.ant-btn-primary {
    background: #fff !important;
    border: 2px solid #000 !important;
  }

  img {
    padding: 10px;
    width: inherit;
    height: inherit;
  }
`;

const PaymentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  flex-wrap: wrap;
`;

const Tab = styled.div`
  border-style: solid;
  border-color: black;
  border-width: 0;
  width: 115px;
  font-size: 14px;

  ${(props) =>
    props.bottom &&
    css`
      border-bottom-width: 1px;
    `};

  ${(props) =>
    props.right &&
    css`
      width: 200px;
    `};

  ${(props) =>
    !props.right &&
    css`
      text-align: right;
    `};

  ${(props) =>
    props.bold &&
    css`
      font-weight: 600;
    `};
`;

const TableContent = styled.div`
  padding-bottom: 130px;

  @media (max-width: 1150px) {
    padding-bottom: 200px;
  }
`;

const Drawer = ({
                  onClose,
                  isOpen,
                  formType,
                  submit,
                  price,
                  transport,
                  voivodeship,
                  voivodeship_selected,
                  paymentType,
                  paymentVendor,
                  changePaymentTypeCallback,
                  changePaymentVendorCallback,
                  t,
                }) => {
  const [form] = Form.useForm();
  const [errorPolicy, setPolicyError] = useState(false);
  const [policy, setPolicy] = useState(false);
  const [spinning, setSpinning] = useState(false);
  const { showModal } = useModal();

  const closeDrawer = () => {
    setPolicyError(false);
    setPolicy(false);
    form.resetFields();
    onClose();
  };

  useEffect(() => {
    // GTM / gtag init
    const script2 = document.createElement('script');
    script2.innerHTML = `
      window.dataLayer = window.dataLayer || [];
      function gtag(){dataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'GTM-W69C6NP7');
    `;
    document.head.appendChild(script2);

    return () => {
      document.head.removeChild(script2);
    };
  }, []);

  const showSuccessModal = (data) => {
    showModal({
      title: 'Payment Successful',
      content: 'Thank you for your purchase. Your payment was successful.',
      data: data,
    });
  };

  // Preserve Klarna return flow via auth_token in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authToken = params.get('auth_token');

    if (authToken && authToken.length > 0) {
      const data = { vendor: 'klarna' };
      showSuccessModal(data);
    }
  }, []);

  const handlePaymentTypeChange = (value) => {
    if (changePaymentTypeCallback) changePaymentTypeCallback(value);
  };

  const handlePaymentVendorChange = (value) => {
    if (changePaymentVendorCallback) changePaymentVendorCallback(value);
  };

  const formProperties = {
    formTitle: '',
    submitButtonText: '',
    formMode: '',
  };

  let { formTitle, submitButtonText, formMode } = formProperties;

  switch (formType) {
    case 'buy':
      formTitle = t('order_form');
      submitButtonText = t('order');
      formMode = 'buy';
      break;
    case 'contact':
      formTitle = t('contact_form');
      submitButtonText = t('send');
      formMode = 'contact';
      break;
    case 'pdfExport':
      formTitle = t('save_as_pdf');
      submitButtonText = t('save');
      formMode = 'pdfExport';
      break;
    default:
      return null;
  }

  const handleSubmit = async (fieldsValue) => {
    // keep explicit policy check like in v3 code
    if (!fieldsValue.policy && formMode !== 'pdfExport') {
      setPolicyError(true);
      return;
    }

    setSpinning(true);

    try {
      const response = await submit(fieldsValue);
      setSpinning(false);

      if (formMode === 'pdfExport') {
        message.success(t('pdf_was_generated'), 5);

        const fileName = response.headers['x-filename'] || 'garage.pdf';
        FileSaver.saveAs(response.data, fileName);
      } else {
        const additional = response?.data?.additional_data;

        if (additional && additional.payment_value === 0) {
          message.success(response.data.message, 5);
          closeDrawer();
          return;
        }

        const firePurchaseEvent = () => {
          if (!additional) return;
          if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
            window.gtag('event', 'purchase', {
              transaction_id: additional.transaction_id,
              value: additional.value,
              items: additional.items,
              currency: additional.currency,
            });
          }
        };

        if (paymentVendor === 'tpay') {
          message.success(response.data.message, 2);

          setTimeout(() => {
            firePurchaseEvent();
            if (additional?.online_payment_url) {
              window.location.href = additional.online_payment_url;
            }
          }, 2000);
        } else if (paymentVendor === 'klarna') {
          message.success(response.data.message, 2);

          setTimeout(() => {
            firePurchaseEvent();
            if (additional?.online_payment_url) {
              window.location.href = additional.online_payment_url;
            }
          }, 2000);
        } else if (paymentVendor === 'cash') {
          message.success(response.data.message, 2);

          setTimeout(() => {
            firePurchaseEvent();
            // gotówka – bez redirecta
          }, 2000);
        } else {
          // bank_transfer (lub fallback)
          setTimeout(() => {
            if (additional?.order) {
              additional.order.vendor = 'bank_transfer';
              showSuccessModal(additional.order);
            }
          }, 1);
        }
      }

      closeDrawer();
    } catch (error) {
      setSpinning(false);
      // możesz dodać komunikat błędu jeśli chcesz
      // message.error(t('submit_failed'));
    }
  };

  const checkPolicy = async (e) => {
    const checked = e.target.checked;
    setPolicy(checked);
    setPolicyError(false);
    try {
      await form.validateFields(['policy']);
    } catch (_) {
      // ignorujemy
    }
  };

  const locale = process.env.REACT_APP_LOCALE;

  const paymentDepositOptions = [
    ...(locale === 'hu' ? [<Option key="0percent" value="0percent">0%</Option>] : []),
    <Option key="30percent" value="30percent">{t('deposit')} - 30%</Option>,
    <Option key="100percent" value="100percent">{t('full_amount')} - 100%</Option>,
  ];

  const defaultPaymentDeposit = locale === 'hu' ? '0percent' : paymentType || '30percent';

  const totalPrice = (price || 0) + (transport || 0);

  const selectedVoivodeshipName =
    voivodeship_selected &&
    voivodeship.find((el) => el.id === voivodeship_selected)?.attributes.name;

  const formattedVoivodeshipName = selectedVoivodeshipName
    ? selectedVoivodeshipName.replace(/^./, (c) => c.toUpperCase())
    : '';

  return (
    <AntDrawer title={formTitle} width={720} onClose={closeDrawer} visible={isOpen}>
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        hideRequiredMark
        initialValues={{
          policy: policy,
          payment_type: defaultPaymentDeposit,
        }}
      >
        <Spin spinning={spinning}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label={t('name')}
                name="first_name"
                rules={[{ required: true, message: t('please_enter_name') }]}
              >
                <Input placeholder={t('please_enter_name')} />
              </Form.Item>
            </Col>
            {formMode !== 'pdfExport' ? (
              <Col span={12}>
                <Form.Item
                  label={t('last_name')}
                  name="last_name"
                  rules={[{ required: true, message: t('please_enter_last_name') }]}
                >
                  <Input placeholder={t('please_enter_last_name')} />
                </Form.Item>
              </Col>
            ) : null}
          </Row>

          <Row gutter={16}>
            {formMode !== 'pdfExport' && voivodeship.length > 0 ? (
              <Col span={12}>
                <Form.Item
                  label={t('address')}
                  name="address"
                  rules={[
                    {
                      required: formMode !== 'pdfExport',
                      message: t('please_enter_address'),
                    },
                  ]}
                >
                  <Input placeholder={t('please_enter_address')} />
                </Form.Item>
              </Col>
            ) : null}
            <Col span={12}>
              {formMode === 'contact' && voivodeship.length > 0 ? (
                <Form.Item
                  label={t('voivodeship')}
                  name="voivodeship"
                  rules={[
                    {
                      required: formMode !== 'pdfExport',
                      message: t('please_select_voivodeship'),
                    },
                  ]}
                >
                  <Select placeholder={t('please_select_voivodeship')}>
                    {voivodeship.map((el) => (
                      <Option key={el.id} value={el.attributes.name}>
                        {el.attributes.name
                          ? el.attributes.name.charAt(0).toUpperCase() +
                          el.attributes.name.slice(1)
                          : ''}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              ) : null}
              {formMode === 'buy' ? (
                <Form.Item
                  label={t('postal_code')}
                  name="postal_code"
                  rules={[{ required: true, message: t('please_enter_postal_code') }]}
                >
                  <Input placeholder={t('please_enter_postal_code')} />
                </Form.Item>
              ) : null}
            </Col>
          </Row>

          {formMode === 'buy' ? (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={t('city')}
                  name="city"
                  rules={[{ required: true, message: t('please_enter_city') }]}
                >
                  <Input placeholder={t('please_enter_city')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label={t('telephone')}
                  name="phone"
                  rules={[{ required: true, message: t('please_enter_telephone') }]}
                >
                  <Input placeholder={t('please_enter_telephone')} />
                </Form.Item>
              </Col>
            </Row>
          ) : null}

          {formMode === 'buy' ? (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: t('please_enter_email') },
                    { type: 'email', message: t('please_enter_valid_email') },
                  ]}
                >
                  <Input placeholder={t('please_enter_email')} />
                </Form.Item>
              </Col>
            </Row>
          ) : null}

          {formMode !== 'buy' ? (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label={t('telephone')}
                  name="phone"
                  rules={[
                    {
                      required: formMode !== 'pdfExport',
                      message: t('please_enter_telephone'),
                    },
                  ]}
                >
                  <Input placeholder={t('please_enter_telephone')} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    {
                      required: formMode !== 'pdfExport',
                      message: t('please_enter_email'),
                    },
                  ]}
                >
                  <Input placeholder={t('please_enter_email')} />
                </Form.Item>
              </Col>
            </Row>
          ) : null}

          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                label={
                  formMode === 'buy' || formMode === 'pdfExport'
                    ? t('comments')
                    : t('describe_garage')
                }
                name="descriptions"
                rules={[
                  {
                    message: t('please_enter_comments'),
                    required: formMode === 'contact',
                  },
                ]}
              >
                <Input.TextArea rows={4} placeholder={t('please_enter_comments')} />
              </Form.Item>
            </Col>
          </Row>

          {formMode === 'buy' && (
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label={t('payment_type')} name="payment_type">
                  <Select onChange={handlePaymentTypeChange} defaultValue={defaultPaymentDeposit}>
                    {paymentDepositOptions}
                  </Select>
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item label={t('online_payment')}>
                  <PaymentWrapper>
                    <PaymentButton
                      type={paymentVendor === 'tpay' ? 'primary' : 'default'}
                      onClick={() => handlePaymentVendorChange('tpay')}
                    >
                      <img src={process.env.PUBLIC_URL + '/statics/tpay_logo_blue.svg'} alt="tpay logo" />
                    </PaymentButton>

                    {process.env.REACT_APP_LOCALE !== 'lt' &&
                      process.env.REACT_APP_DOMAIN !== 'ESTAL' && (
                        <PaymentButton
                          type={paymentVendor === 'klarna' ? 'primary' : 'default'}
                          onClick={() => handlePaymentVendorChange('klarna')}
                        >
                          <img
                            src={process.env.PUBLIC_URL + '/statics/klarna-logo.png'}
                            alt="klarna logo"
                          />
                        </PaymentButton>
                      )}

                    {process.env.REACT_APP_LOCALE === 'hu' &&
                      process.env.REACT_APP_DOMAIN !== 'ESTAL' && (
                        <PaymentButton
                          type={paymentVendor === 'cash' ? 'primary' : 'default'}
                          onClick={() => handlePaymentVendorChange('cash')}
                          style={{
                            display: 'flex',
                            flexFlow: 'column',
                            color: 'black',
                            fontSize: 11,
                          }}
                        >
                          <span className="text-black">{t('cash')}</span>
                        </PaymentButton>
                      )}

                    <PaymentButton
                      type={paymentVendor === 'bank_transfer' ? 'primary' : 'default'}
                      onClick={() => handlePaymentVendorChange('bank_transfer')}
                      style={{
                        display: 'flex',
                        flexFlow: 'column',
                        color: 'black',
                        fontSize: 11,
                      }}
                    >
                      <span className="text-black">{t('bank_transfer')}</span>
                    </PaymentButton>
                  </PaymentWrapper>
                </Form.Item>
              </Col>
            </Row>
          )}
        </Spin>

        {formMode === 'buy' ? (
          <TableContent>
            <TabRow>
              <Tab bottom right>
                {t('garage_price')}:
              </Tab>
              <Tab bottom>
                {price}
                {process.env.REACT_APP_DISPLAY_CURRENCY}
              </Tab>
            </TabRow>
            <TabRow>
              <Tab bottom right>
                {t('transportation')} (
                {formattedVoivodeshipName}
                ):
              </Tab>
              <Tab bottom>
                {transport}
                {process.env.REACT_APP_DISPLAY_CURRENCY}
              </Tab>
            </TabRow>
            <TabRow>
              <Tab right bold>
                {t('sum')}:
              </Tab>
              <Tab bold>
                {totalPrice}
                {process.env.REACT_APP_DISPLAY_CURRENCY}
              </Tab>
            </TabRow>
          </TableContent>
        ) : null}

        <div
          style={{
            position: 'absolute',
            left: 0,
            display: 'flex',
            justifyContent: 'flex-start',
            bottom: 52,
            width: '100%',
            padding: '10px 16px',
            background: colors.drawerBackground,
            textAlign: 'right',
          }}
        >
          {formMode !== 'pdfExport' ? (
            <Form.Item
              name="policy"
              valuePropName="checked"
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Checkbox onChange={checkPolicy}>
                {t('i_consent_to_the_processing')}{' '}
                <b>
                  <a
                    href="https://www.garazeblaszane.net.pl/polityka-prywatnosci/"
                    target="_blank"
                    rel="noreferrer"
                  >
                    {t('privacy_policy')}
                  </a>
                </b>
              </Checkbox>
            </Form.Item>
          ) : null}
          {errorPolicy && !policy ? (
            <div style={{ color: colors.inValidFormColor }} className="ant-form-explain">
              {t('necessary_to_accept')}
            </div>
          ) : null}
        </div>

        <div
          style={{
            position: 'absolute',
            left: 0,
            bottom: 0,
            height: 88,
            width: '100%',
            borderTop: `1px solid ${colors.secondaryBorderColor}`,
            padding: '10px 16px',
            background: colors.drawerBackground,
            textAlign: 'right',
          }}
        >
          <Button onClick={closeDrawer} style={{ marginRight: 8, color: colors.main }}>
            {t('cancel')}
          </Button>
          <Button type="primary" htmlType="submit" loading={spinning}>
            {submitButtonText}
          </Button>
        </div>
      </Form>
    </AntDrawer>
  );
};

Drawer.propTypes = {
  formType: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]).isRequired,
  isOpen: PropTypes.bool.isRequired,
  submit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  changePaymentTypeCallback: PropTypes.func,
  changePaymentVendorCallback: PropTypes.func,
  paymentType: PropTypes.string,
  paymentVendor: PropTypes.string,
  price: PropTypes.number,
  transport: PropTypes.number,
  voivodeship: PropTypes.arrayOf(PropTypes.object),
  voivodeship_selected: PropTypes.number,
  t: PropTypes.func.isRequired,
};

Drawer.defaultProps = {
  voivodeship: [],
  price: null,
  transport: null,
  voivodeship_selected: null,
  paymentType: null,
  paymentVendor: null,
  changePaymentTypeCallback: null,
  changePaymentVendorCallback: null,
};

const mapDispatchToProps = {
  onClose: close,
  submit: sendForm,
  changePaymentTypeCallback: changePaymentType,
  changePaymentVendorCallback: changePaymentVendor,
};

export default connect(selector, mapDispatchToProps)(withTranslation(Drawer));
